<template>
  <div class="manager-submissions">
    <div class="hr-metrics-grid hr-mb-4">
      <div class="hr-metric-card gradient-4">
        <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
        <div class="hr-metric-content">
              <p class="hr-metric-label">Awaiting Review</p>
        <h4 class="hr-metric-value">{{ statistics.pending }}</h4>
            </div>
      </div>
      <div class="hr-metric-card gradient-3">
        <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
        <div class="hr-metric-content">
              <p class="hr-metric-label">Approved</p>
        <h4 class="hr-metric-value">{{ statistics.approved }}</h4>
            </div>
      </div>
      <div class="hr-metric-card gradient-2">
        <div class="hr-metric-icon"><i class="bx bx-revision"></i></div>
        <div class="hr-metric-content">
              <p class="hr-metric-label">Returned</p>
        <h4 class="hr-metric-value">{{ statistics.returned }}</h4>
            </div>
      </div>
      <div class="hr-metric-card gradient-1">
        <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
        <div class="hr-metric-content">
              <p class="hr-metric-label">Total</p>
        <h4 class="hr-metric-value">{{ statistics.total }}</h4>
            </div>
      </div>
    </div>

    <div class="hr-chart-card hr-mb-3">
      <div class="hr-chart-body">
        <div class="filters">
          <select v-model="statusFilter" class="hr-select" @change="loadSubmissions">
            <option value="">All statuses</option>
            <option value="Submitted">Pending my review</option>
            <option value="Approved">Approved</option>
            <option value="Returned">Returned</option>
            <option value="Rejected">Rejected</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="loading" class="hr-loading-wrap hr-py-5"><div class="hr-spinner"></div></div>

    <div v-else class="hr-chart-card">
      <div class="hr-chart-body">
        <div class="hr-table-wrapper responsive-table-wrap">
          <table class="hr-table hr-table-hover">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Form</th>
                <th>Department</th>
                <th>Status</th>
                <th>Submitted</th>
                <th class="hr-text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in submissions" :key="row.id">
                <td>
                  <div class="hr-fw-medium">{{ row.employee_name }}</div>
                  <small class="hr-text-muted">{{ row.emp_no }}</small>
                </td>
                <td>
                  <div>{{ row.form_title }}</div>
                  <small class="hr-text-muted">{{ row.appraisal_year }} {{ row.period_label }}</small>
                </td>
                <td>{{ row.department || '—' }}</td>
                <td><span class="hr-badge" :class="statusClass(row.status)">{{ row.status }}</span></td>
                <td>{{ row.submitted_at || '—' }}</td>
                <td class="hr-text-end">
                  <div class="hr-action-btns">
                    <button class="hr-btn hr-btn-sm hr-btn-info" @click="viewPdf(row)" title="View PDF">
                      <i class="bx bx-file"></i>
                    </button>
                    <button
                      v-if="row.status === 'Submitted'"
                      class="hr-btn hr-btn-sm hr-btn-primary"
                      @click="openReview(row)"
                    >
                      Review
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="submissions.length === 0">
                <td colspan="6" class="hr-text-center hr-py-4 hr-text-muted">No team submissions found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="reviewRow" class="modal-backdrop" @click.self="closeReview">
      <div class="modal-panel">
        <div class="modal-header">
          <h3>Review: {{ reviewRow.employee_name }}</h3>
          <button type="button" class="modal-close" @click="closeReview"><i class="bx bx-x"></i></button>
        </div>
        <div class="modal-body">
          <p><strong>Form:</strong> {{ reviewRow.form_title }}</p>
          <p v-if="reviewRow.employee_notes"><strong>Employee notes:</strong> {{ reviewRow.employee_notes }}</p>
          <div class="hr-form-group hr-mt-3">
            <label class="hr-form-label">Supervisor comments</label>
            <textarea v-model="reviewNotes" class="hr-input" rows="4"></textarea>
          </div>
          <div class="hr-form-group">
            <label class="hr-form-label">Rating (optional, 1–5)</label>
            <input v-model="reviewRating" type="number" min="1" max="5" step="0.1" class="hr-input" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="hr-btn hr-btn-secondary" @click="closeReview">Cancel</button>
          <button class="hr-btn hr-btn-warning" :disabled="reviewing" @click="submitReview('return')">Return for revision</button>
          <button class="hr-btn hr-btn-danger" :disabled="reviewing" @click="submitReview('reject')">Reject</button>
          <button class="hr-btn hr-btn-success" :disabled="reviewing" @click="submitReview('approve')">Approve</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "ManagerAppraisalSubmissions",
  data() {
    return {
      loading: false,
      reviewing: false,
      submissions: [],
      statistics: { pending: 0, approved: 0, returned: 0, total: 0 },
      statusFilter: "Submitted",
      reviewRow: null,
      reviewNotes: "",
      reviewRating: "",
    };
  },
  mounted() {
    this.loadSubmissions();
  },
  methods: {
    async loadSubmissions() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getSupervisorAppraisalPdfSubmissions");
        if (this.statusFilter) data.append("status", this.statusFilter);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.submissions = res.data.data?.submissions || [];
          this.statistics = res.data.data?.statistics || this.statistics;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    statusClass(status) {
      const map = {
        Submitted: "hr-badge-warning",
        Approved: "hr-badge-success",
        Returned: "hr-badge-info",
        Rejected: "hr-badge-danger",
        Draft: "hr-badge-secondary",
      };
      return map[status] || "hr-badge-secondary";
    },
    async viewPdf(row) {
      try {
        const data = new FormData();
        data.append("function", "downloadAppraisalPdfFile");
        data.append("type", "completed");
        data.append("submissionId", row.id);
        const res = await executeDownload(data, authUrl);
        const blob = new Blob([res.data], { type: "application/pdf" });
        window.open(window.URL.createObjectURL(blob), "_blank");
      } catch (e) {
        this.$toast?.error("Could not open PDF") || alert("Could not open PDF");
      }
    },
    openReview(row) {
      this.reviewRow = row;
      this.reviewNotes = "";
      this.reviewRating = "";
    },
    closeReview() {
      this.reviewRow = null;
    },
    async submitReview(action) {
      if (!this.reviewRow) return;
      this.reviewing = true;
      try {
        const data = new FormData();
        data.append("function", "reviewAppraisalPdfSubmission");
        data.append("submissionId", this.reviewRow.id);
        data.append("action", action);
        data.append("supervisorNotes", this.reviewNotes);
        if (this.reviewRating) data.append("supervisorRating", this.reviewRating);
        const res = await execute(data, authUrl);
        if (!res?.data?.success) throw new Error(res?.data?.message || "Review failed");
        this.$toast?.success("Review saved") || alert("Review saved");
        this.closeReview();
        this.loadSubmissions();
      } catch (e) {
        this.$toast?.error(e.message) || alert(e.message);
      } finally {
        this.reviewing = false;
      }
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.filters { max-width: 280px; }
.hr-text-end { text-align: right; }
.hr-action-btns { display: flex; gap: 6px; justify-content: flex-end; flex-wrap: wrap; }
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px;
}
.modal-panel {
  width: min(640px, 100%); background: #fff; border-radius: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}
.modal-header, .modal-footer {
  padding: 16px 20px; border-bottom: 1px solid #e2e8f0;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;
}
.modal-footer { border-bottom: none; border-top: 1px solid #e2e8f0; justify-content: flex-end; }
.modal-body { padding: 20px; }
.modal-close { border: none; background: transparent; font-size: 24px; cursor: pointer; }
.hr-btn-success { background: #16a34a; color: #fff; border: none; }
.hr-btn-warning { background: #f59e0b; color: #fff; border: none; }
</style>
